import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import accountDefaultImage from '../assets/images/sidebar/account_default_icon.png';
import deskIcon from '../assets/images/sidebar/desk_icon.png';
import analysisIcon from '../assets/images/common/search-calendar-icon.png';
import { SparkleIcon } from '../components/icons/SparkleIcon';
import { MessageIcon } from '../components/icons/MessageIcon';
import { MessageOpenIcon } from '../components/icons/MessageOpenIcon';
import settingIcon from '../assets/images/sidebar/settings_icon.png';
import kotomilLogo from '../assets/images/common/kotomil-logo.png';
import { useUser } from '@/hooks/UserContext';

import { isReallyBlank } from '@/utils/utils';
import { Link, useLocation } from 'react-router-dom';
import { buildPlansUrlWithTracking } from '@/utils/openPlansWithTracking';
import { useNotificationSidebar } from '@/contexts/NotificationSidebarContext';

interface NavItem {
    labelKey: string;
    icon: string | React.ComponentType<{ className?: string }>;
    href: string;
    beta?: boolean;
}

const navItems: NavItem[] = [
    { labelKey: 'nav.desk', icon: deskIcon, href: '/desk' },
    { labelKey: 'nav.analysis', icon: analysisIcon, href: '/analysis' },
    { labelKey: 'nav.delivery', icon: MessageOpenIcon, href: '/delivery', beta: true },
];

/**
 * サイドバー
 * @returns
 */
const Sidebar: React.FC = () => {
    const { t, i18n } = useTranslation('sidebar');
    const { pathname } = useLocation();
    const [accountImage, setAccountImage] = useState<string>('');
    const [accountName, setAccountName] = useState<string>('');
    const [planName, setPlanName] = useState<string>('');
    const [displayImage, setDisplayImage] = useState<string>(accountDefaultImage);
    const [imageError, setImageError] = useState<boolean>(false);
    const { isOpen, openSidebar, closeSidebar, hasUnread } = useNotificationSidebar();
    const { userInfo, isLoading } = useUser();

    /**
     * ユーザー情報が更新されたら表示を更新
     */
    useEffect(() => {
        if (userInfo) {
            setAccountImage(userInfo.accountImage || '');
            setAccountName(userInfo.accountName || '');
            setPlanName(userInfo.planName || '');
        }
    }, [userInfo]);

    /**
     * アカウント画像の表示を管理
     */
    useEffect(() => {
        if (accountImage && accountImage.trim() !== '' && !imageError) {
            setDisplayImage(accountImage);
        } else {
            setDisplayImage(accountDefaultImage);
        }
    }, [accountImage, imageError]);

    // レンダリング
    return (
            <aside id="sidebar" className="flex flex-col bg-[#FFD700] text-black w-48 h-screen overflow-y-auto p-5 gap-6 rounded-r-xl fixed top-0 left-0 z-10 shadow-md">
                {/* ロゴ */}
                <div className="flex items-center">
                    <img src={kotomilLogo} className="size-6 ms-2 me-4 image-protection" />
                    <div className="flex flex-col">
                        <p className="text-xl tracking-widest">{t('brand.title')}</p>
                        {i18n.language?.startsWith('ja') && (
                            <p className="text-sm tracking-normal">{t('brand.subtitle')}</p>
                        )}
                    </div>
                </div>

                <hr className="border-white/70" />

                {/* アカウント */}
                <div className="flex items-center gap-3">
                    {/* アカウント画像 */}
                    <div className="relative inline-block">
                        {isLoading ? (
                            <div className="w-10 h-10 rounded-lg bg-white/30 animate-pulse" />
                        ) : (
                            <img
                                src={displayImage}
                                alt={t('account.imageAlt')}
                                className="w-10 h-10 rounded-lg image-protection"
                                onError={(e) => {
                                    // 読み込みエラー時はデフォルト画像に切り替え（一度だけ）
                                    if (e.currentTarget.src !== accountDefaultImage) {
                                        e.currentTarget.src = accountDefaultImage;
                                        setImageError(true);
                                    }
                                }}
                            />
                        )}
                        {/* プラン名バッジ */}
                        {!isLoading && !isReallyBlank(planName) && planName === 'basic_plan' && (
                            <span className="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2 text-[10px] border-2 border-white text-black leading-[10px] tracking-wider bg-kotomilThemaColor rounded-full px-1 pt-0.5 pb-[3px]">
                                {t('badges.basicPlan')}
                            </span>
                        )}
                    </div>
                    {/* アカウント名 */}
                    {isLoading ? (
                        <div className="h-4 w-20 bg-white/30 animate-pulse rounded" />
                    ) : (
                        <p className="break-words">{accountName || t('account.noName')}</p>
                    )}
                </div>

                {/* メッセージ */}
                <button
                    onClick={() => {
                        if (isOpen) {
                            closeSidebar();
                        } else {
                            openSidebar();
                        }
                    }}
                    className="flex items-center gap-4 rounded-xl px-4 py-3 font-medium w-full text-left text-sm relative hover:bg-white/20"
                >
                    <div className="relative">
                        {isOpen ? (
                            <MessageOpenIcon className="size-6 text-black" />
                        ) : (
                            <MessageIcon className="size-6 text-black" />
                        )}
                        {/* 未読バッジ */}
                        {hasUnread && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-kotomilThemaColor border-2 border-white rounded-full flex-shrink-0" />
                        )}
                    </div>
                    <span>{t('messages')}</span>
                </button>

                <hr className="border-white/70" />

                {/* ナビゲーション */}
                <nav className="flex-1 flex flex-col gap-2">
                    {navItems.map(({ labelKey, icon: Icon, href, beta }) => (
                        <Link
                            key={href}
                            id={href === '/desk' ? 'sidebar-nav-desk' : href === '/analysis' ? 'sidebar-nav-analysis' : href === '/delivery' ? 'sidebar-nav-delivery' : undefined}
                            to={href}
                            className={clsx(
                                'group flex items-center gap-4 rounded-xl px-4 py-3 font-medium hover:bg-white/30 transition',
                                pathname === href && 'bg-white/30',
                            )}
                        >
                            {typeof Icon === 'string' ? (
                                <img src={Icon} className="size-7 image-protection shrink-0" />
                            ) : (
                                <Icon className="size-7 text-black shrink-0" />
                            )}
                            <span className="flex flex-col gap-1 min-w-0 flex-1">
                                <span className="truncate leading-snug">{t(labelKey)}</span>
                                {beta && (
                                    <span className="self-start text-[10px] font-semibold leading-none text-white bg-neutral-800 rounded px-1.5 py-0.5 border border-white/40">
                                        {t('badges.beta')}
                                    </span>
                                )}
                            </span>
                        </Link>
                    ))}

                    <hr className="border-white/70 mt-4 mb-2" />

                    {/* アップグレードボタン（ベーシック以外に表示） */}
                    {planName !== 'basic_plan' && (
                        <Link
                            to={buildPlansUrlWithTracking({ source: 'sidebar_upgrade_button' })}
                            className="mt-4 flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-bold text-black transition bg-white/25 border border-white/50 backdrop-blur-md hover:bg-white/35"
                        >
                            <SparkleIcon className="size-5 text-black" />
                            <span className="whitespace-nowrap text-black">{t('upgrade')}</span>
                        </Link>
                    )}
                </nav>

                {/* 設定 */}
                <Link
                    to="/settings"
                    className={clsx(
                        'flex items-center gap-4 rounded-xl mt-4 px-4 py-3 font-medium hover:bg-white/30 transition',
                        pathname === '/settings' && 'bg-white/30',
                    )}
                >
                    <img src={settingIcon} className="size-6 image-protection" />
                    <span>{t('nav.settings')}</span>
                </Link>
            </aside>
    );
};

export default Sidebar;
